import { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  ConditionDataSource,
  ConditionExpression,
} from "@redotech/redo-flows-common/steps";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { RedoMarketingRpcClient } from "@redotech/redo-merchant-marketing-rpc-client/client";
import { createContext, ReactNode, useContext } from "react";
import { DeepPartial } from "../steps";

/**
 * Registry of id→name label fetchers for branch matchValue display.
 *
 * To support a new token field, add an entry here. The provider fetches
 * every entry once per flow editor mount; nodes read per-field labels via
 * useConditionValueLabels with zero per-node RPCs.
 */
const FETCHERS: {
  field: string;
  fetch: (clients: {
    merchant: RedoMerchantRpcClient;
    marketing: RedoMarketingRpcClient | undefined;
  }) => Promise<Map<string, string>>;
}[] = [
  {
    field: "fulfillmentLocation",
    fetch: async ({ merchant }) => {
      const { locations } = await merchant.getLocations(null);
      return new Map(locations.map((l) => [l.externalId, l.name]));
    },
  },
  {
    field: "form",
    fetch: async ({ marketing }) => {
      if (!marketing) {
        return new Map();
      }
      const forms = await marketing.getSignupFormOptionsMinimal({});
      return new Map(forms.map((f) => [f.id, f.name]));
    },
  },
];

const ConditionValueLabelsContext = createContext<
  Map<string, Map<string, string>> | undefined
>(undefined);

export function ConditionValueLabelsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const marketingRpcClient = useContext(RedoMarketingRpcClientContext);

  const load = useLoad(async (): Promise<Map<string, Map<string, string>>> => {
    const entries = await Promise.all(
      FETCHERS.map(
        async (f) =>
          [
            f.field,
            await f.fetch({
              merchant: merchantRpcClient,
              marketing: marketingRpcClient,
            }),
          ] as const,
      ),
    );
    return new Map(entries);
  }, [merchantRpcClient, marketingRpcClient]);

  return (
    <ConditionValueLabelsContext.Provider value={load.value}>
      {children}
    </ConditionValueLabelsContext.Provider>
  );
}

export function useConditionValueLabels(
  expression: DeepPartial<ConditionExpression> | undefined,
): Map<string, string> | undefined {
  const allLabels = useContext(ConditionValueLabelsContext);
  if (
    expression?.dataSource !== ConditionDataSource.TriggerData ||
    expression.schemaBooleanExpression?.field == null
  ) {
    return undefined;
  }
  return allLabels?.get(expression.schemaBooleanExpression.field.toString());
}
